import { useState } from 'react';
import { AppIdea } from '@/hooks/app-library';
import { generatePDFFromHTML, markdownToHTML, downloadPDF } from '@/utils/pdf-creator';
import { toast } from '@/hooks/errors/use-toast';

export const useAppIdeaPDF = () => {
  const [isGenerating, setIsGenerating] = useState(false);

  const generatePDF = async (idea: AppIdea) => {
    setIsGenerating(true);
    
    try {
      // Generate the same markdown content as the current download functionality
      const markdownContent = `# ${idea.name || idea.generated_title || 'App Idea'}

**Category:** ${idea.category || 'Unknown'}

**Problem:** ${idea.original_problem}

**Description:** ${idea.description || idea.generated_description || 'No description available'}

**Created:** ${new Date(idea.created_at).toLocaleDateString()}

---

## Research Overview
${idea.research_overview || 'No research overview available'}

## Requirements
${idea.research_requirements || 'No requirements available'}

## Enhanced Features
${idea.research_enhanced_features || 'No enhanced features available'}`;

      // Convert markdown to HTML with enhanced styling
      const htmlContent = markdownToHTML(markdownContent);
      
      // Generate PDF filename
      const filename = `${(idea.name || idea.generated_title || 'app_idea').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`;
      
      // Generate PDF
      const result = await generatePDFFromHTML(htmlContent, {
        filename,
        format: 'a4',
        orientation: 'portrait',
        margin: { top: 20, right: 20, bottom: 20, left: 20 },
        quality: 0.95
      });

      if (result.success && result.blob && result.filename) {
        downloadPDF(result.blob, result.filename);
        
        toast({
          title: "PDF Generated Successfully",
          description: "Your app idea has been downloaded as a PDF file",
        });
      } else {
        throw new Error(result.error || 'Failed to generate PDF');
      }
    } catch (error) {
      console.error('PDF generation failed:', error);
      
      toast({
        title: "PDF Generation Failed",
        description: error instanceof Error ? error.message : "Failed to generate PDF. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return { generatePDF, isGenerating };
}; 